import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, GripVertical, Settings2, RotateCcw, Sparkles, Eye, EyeOff, SlidersHorizontal } from "lucide-react";
import ConfigWidgetEditor from "./ConfigWidgetEditor";
import SortableList from "@/components/SortableList";

const TIPI = [
  { value: "number", label: "Numero" },
  { value: "text", label: "Testo breve" },
  { value: "textarea", label: "Testo lungo" },
  { value: "enum", label: "Enum (select)" },
  { value: "bool", label: "Sì/No" },
  { value: "date", label: "Data" },
  { value: "array_ripetibile", label: "Array ripetibile (avanzato)" },
  { value: "tabella_mensile", label: "Tabella mensile (avanzato)" },
];

const TARGET_KEYS = [
  { value: "__none__", label: "— nessuno —" },
  { value: "booking_target", label: "Booking target" },
  { value: "pulizia_booking_target", label: "Pulizia Booking target" },
  { value: "google_target", label: "Google target" },
  { value: "transfer_annuale", label: "Transfer annuale" },
  { value: "food_annuale", label: "Food annuale" },
  { value: "food_annuale_mese", label: "Food annuale ÷ 12" },
  { value: "fatturato_annuale", label: "Fatturato annuale" },
];

export default function ConfigurazioneSchemaRelazioni() {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selSez, setSelSez] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nuovaSezOpen, setNuovaSezOpen] = useState(false);
  const [nuovaSezNome, setNuovaSezNome] = useState("");
  const [nuovoCampoOpen, setNuovoCampoOpen] = useState(false);
  const [nuovoCampo, setNuovoCampo] = useState({ label: "", tipo: "number", required: false, target_key_hotel: "", ai_include: true });
  const [editCampoOpen, setEditCampoOpen] = useState(false);
  const [editCampo, setEditCampo] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [configEditor, setConfigEditor] = useState({ open: false, campo: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/report-schema");
      setSchema(data);
      if (data.sezioni?.length && !selSez) setSelSez(data.sezioni[0].key);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [selSez]);
  useEffect(() => { load(); }, [load]);

  const currentSez = schema?.sezioni?.find((s) => s.key === selSez);

  const patchSezione = async (key, updates) => {
    setBusy(true);
    try {
      await api.patch(`/admin/report-schema/sezioni/${key}`, updates);
      await load();
      toast.success("Sezione aggiornata");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const deleteSezione = async (key) => {
    if (!confirm(`Rimuovere la sezione "${key}"? I dati compilati resteranno nelle relazioni ma non saranno più visibili.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/report-schema/sezioni/${key}`);
      await load();
      setSelSez(schema.sezioni.find((s) => s.key !== key)?.key || null);
      toast.success("Sezione rimossa");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const reorderSezioni = async (newList) => {
    setSchema((prev) => ({ ...prev, sezioni: newList.map((s, i) => ({ ...s, ordine: i + 1 })) }));
    setBusy(true);
    try {
      await api.patch("/admin/report-schema/sezioni/reorder", {
        ordered_keys: newList.map((s) => s.key),
      });
      toast.success("Ordine sezioni aggiornato");
    } catch (e) {
      toast.error(formatApiError(e));
      await load();
    } finally { setBusy(false); }
  };

  const addSezione = async () => {
    if (!nuovaSezNome.trim()) return toast.error("Nome obbligatorio");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/report-schema/sezioni", { nome: nuovaSezNome.trim() });
      await load();
      setSelSez(data.sezione.key);
      setNuovaSezOpen(false); setNuovaSezNome("");
      toast.success("Sezione creata");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const addCampo = async () => {
    if (!nuovoCampo.label.trim()) return toast.error("Label obbligatorio");
    if (nuovoCampo.tipo === "enum") return toast.error("Enum: aggiungi il campo poi modifica le opzioni");
    setBusy(true);
    try {
      await api.post(`/admin/report-schema/sezioni/${selSez}/campi`, nuovoCampo);
      await load();
      setNuovoCampoOpen(false);
      setNuovoCampo({ label: "", tipo: "number", required: false, target_key_hotel: "", ai_include: true });
      toast.success("Campo aggiunto");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const savePatchCampo = async () => {
    if (!editCampo) return;
    setBusy(true);
    try {
      const { key, ...rest } = editCampo;
      await api.patch(`/admin/report-schema/sezioni/${selSez}/campi/${key}`, rest);
      await load();
      setEditCampoOpen(false); setEditCampo(null);
      toast.success("Campo aggiornato");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const deleteCampo = async (campoKey) => {
    if (!confirm(`Rimuovere il campo "${campoKey}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/report-schema/sezioni/${selSez}/campi/${campoKey}`);
      await load();
      toast.success("Campo rimosso");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const reorderCampi = async (newList) => {
    setSchema((prev) => {
      const next = { ...prev };
      next.sezioni = (prev.sezioni || []).map((s) => {
        if (s.key !== selSez) return s;
        return { ...s, campi: newList.map((c, i) => ({ ...c, ordine: i + 1 })) };
      });
      return next;
    });
    setBusy(true);
    try {
      await api.patch(`/admin/report-schema/sezioni/${selSez}/campi/reorder`, {
        ordered_keys: newList.map((c) => c.key),
      });
      toast.success("Ordine campi aggiornato");
    } catch (e) {
      toast.error(formatApiError(e));
      await load();
    } finally { setBusy(false); }
  };

  const resetAll = async () => {
    setBusy(true);
    try {
      await api.post("/admin/report-schema/reset");
      await load();
      setResetOpen(false);
      toast.success("Schema ripristinato ai default");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-6 w-6 animate-spin text-lavender-500" /></div>;

  const sorted = (arr) => [...(arr || [])].sort((a, b) => (a.ordine || 0) - (b.ordine || 0));

  return (
    <div className="space-y-4" data-testid="config-schema-relazioni">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display tracking-tight flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-lavender-600" /> Schema Relazioni Mensili
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configura sezioni e campi delle relazioni. Le chiavi tecniche (<code className="text-xs">key</code>) sono immutabili una volta create. Le relazioni inviate conservano uno snapshot dello schema.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground tabular-nums">v{schema?.version || 1}</span>
          <Button variant="outline" size="sm" onClick={() => setResetOpen(true)} data-testid="reset-schema-btn">
            <RotateCcw className="h-4 w-4" /> Ripristina default
          </Button>
          <Button size="sm" onClick={() => setNuovaSezOpen(true)} data-testid="add-sezione-btn">
            <Plus className="h-4 w-4" /> Nuova sezione
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        {/* Colonna sinistra: elenco sezioni riordinabile via drag&drop */}
        <Card className="p-3 space-y-1">
          <SortableList
            items={sorted(schema?.sezioni)}
            getId={(s) => s.key}
            onReorder={reorderSezioni}
          >
            {(s, sp) => (
              <div
                ref={sp.setNodeRef}
                style={sp.style}
                className={`flex items-center gap-1 rounded-lg px-2 py-1.5 ${s.key === selSez ? "bg-lavender-500 text-white" : "hover:bg-accent"}`}
                data-testid={`sez-item-${s.key}`}
              >
                <button
                  {...sp.attributes}
                  {...sp.listeners}
                  className={`p-1 cursor-grab active:cursor-grabbing touch-none ${s.key === selSez ? "text-white/70" : "text-muted-foreground opacity-70 hover:opacity-100"}`}
                  aria-label={`Trascina sezione ${s.nome}`}
                  data-testid={`sez-handle-${s.key}`}
                >
                  <GripVertical className="h-4 w-4" />
                </button>
                <button onClick={() => setSelSez(s.key)} className="flex-1 text-left text-sm truncate">
                  {s.nome}
                  <span className={`ml-1.5 text-[9px] ${s.key === selSez ? "text-white/70" : "text-muted-foreground"}`}>
                    {(s.campi || []).length} campi
                    {s.system && " · system"}
                    {!s.visibile && " · nascosta"}
                  </span>
                </button>
              </div>
            )}
          </SortableList>
        </Card>

        {/* Colonna destra: dettaglio sezione */}
        {currentSez ? (
          <Card className="p-5 space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <h2 className="font-display text-lg tracking-tight">{currentSez.nome}</h2>
                <div className="text-xs text-muted-foreground mt-0.5">
                  key: <code className="text-[10px] px-1 py-0.5 rounded bg-accent/40">{currentSez.key}</code>
                  {currentSez.system && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200">SYSTEM</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => patchSezione(currentSez.key, { visibile: !currentSez.visibile })} data-testid="toggle-sez-visibile">
                  {currentSez.visibile ? <><Eye className="h-3.5 w-3.5" /> Visibile</> : <><EyeOff className="h-3.5 w-3.5" /> Nascosta</>}
                </Button>
                {!currentSez.system && (
                  <Button variant="outline" size="sm" onClick={() => deleteSezione(currentSez.key)} className="text-rose-500" data-testid="delete-sez">
                    <Trash2 className="h-3.5 w-3.5" /> Rimuovi
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Nome visualizzato</Label>
                <Input defaultValue={currentSez.nome} onBlur={(e) => e.target.value !== currentSez.nome && patchSezione(currentSez.key, { nome: e.target.value })} data-testid="sez-nome" />
              </div>
              <div className="space-y-1.5">
                <Label>Descrizione</Label>
                <Input defaultValue={currentSez.descrizione || ""} onBlur={(e) => e.target.value !== (currentSez.descrizione || "") && patchSezione(currentSez.key, { descrizione: e.target.value })} />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={currentSez.ai_include !== false} onCheckedChange={(v) => patchSezione(currentSez.key, { ai_include: v })} />
                <Label className="text-xs">Includi in AI (generate-section / conclusioni)</Label>
              </div>
            </div>

            <div className="border-t border-border/60 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-sm">Campi ({(currentSez.campi || []).length})</h3>
                <Button size="sm" onClick={() => setNuovoCampoOpen(true)} data-testid="add-campo-btn">
                  <Plus className="h-3.5 w-3.5" /> Aggiungi campo
                </Button>
              </div>
              <div className="space-y-1.5">
                <SortableList
                  items={sorted(currentSez.campi)}
                  getId={(c) => c.key}
                  onReorder={reorderCampi}
                >
                  {(c, sp) => (
                    <div
                      ref={sp.setNodeRef}
                      style={sp.style}
                      className="flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2 bg-background/40"
                      data-testid={`campo-item-${c.key}`}
                    >
                      <button
                        {...sp.attributes}
                        {...sp.listeners}
                        className="p-1 cursor-grab active:cursor-grabbing touch-none text-muted-foreground opacity-70 hover:opacity-100"
                        aria-label={`Trascina campo ${c.label}`}
                        data-testid={`campo-handle-${c.key}`}
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{c.label}</span>
                          <code className="text-[10px] px-1 py-0.5 rounded bg-accent/40">{c.key}</code>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-lavender-100 text-lavender-700 dark:bg-lavender-950/40">{c.tipo}</span>
                          {c.required && <span className="text-[9px] uppercase text-rose-500 font-medium">obbligatorio</span>}
                          {c.target_key_hotel && <span className="text-[9px] text-emerald-600">target: {c.target_key_hotel}</span>}
                          {c.ai_include === false && <span className="text-[9px] text-muted-foreground">no-ai</span>}
                          {c.ruolo_edit?.length > 0 && <span className="text-[9px] text-amber-600">ruoli: {c.ruolo_edit.join(",")}</span>}
                        </div>
                        {c.help_text && <div className="text-[10px] text-muted-foreground mt-0.5">{c.help_text}</div>}
                      </div>
                      {(c.tipo === "tabella_mensile" || c.tipo === "array_ripetibile") && (
                        <Button variant="ghost" size="sm" onClick={() => setConfigEditor({ open: true, campo: c })} data-testid={`config-widget-${c.key}`} className="text-lavender-700 dark:text-lavender-300">
                          <SlidersHorizontal className="h-3.5 w-3.5" /> Config
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => { setEditCampo({ ...c }); setEditCampoOpen(true); }} data-testid={`edit-campo-${c.key}`}>Modifica</Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteCampo(c.key)} className="text-rose-500"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  )}
                </SortableList>
                {(currentSez.campi || []).length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">Nessun campo. Aggiungi il primo.</p>
                )}
              </div>
            </div>
          </Card>
        ) : (
          <Card className="p-8 text-center text-muted-foreground text-sm">Seleziona una sezione dall&apos;elenco.</Card>
        )}
      </div>

      {/* Modal: nuova sezione */}
      <Dialog open={nuovaSezOpen} onOpenChange={setNuovaSezOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Nuova sezione</DialogTitle><DialogDescription>La chiave tecnica viene generata dal nome (immutabile).</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <Label>Nome</Label>
            <Input value={nuovaSezNome} onChange={(e) => setNuovaSezNome(e.target.value)} placeholder="Es. Spa & Wellness" data-testid="new-sez-nome" autoFocus />
            {nuovaSezNome && <p className="text-[10px] text-muted-foreground">key: <code>{nuovaSezNome.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "")}</code></p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNuovaSezOpen(false)}>Annulla</Button>
            <Button onClick={addSezione} disabled={busy} data-testid="new-sez-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: nuovo campo */}
      <Dialog open={nuovoCampoOpen} onOpenChange={setNuovoCampoOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Nuovo campo</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Label</Label>
            <Input value={nuovoCampo.label} onChange={(e) => setNuovoCampo({ ...nuovoCampo, label: e.target.value })} data-testid="new-campo-label" autoFocus />
            <Label>Tipo</Label>
            <Select value={nuovoCampo.tipo} onValueChange={(v) => setNuovoCampo({ ...nuovoCampo, tipo: v })}>
              <SelectTrigger data-testid="new-campo-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>{TIPI.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
            {nuovoCampo.tipo === "number" && (
              <>
                <Label>Target hotel (confronto rosso/verde, opzionale)</Label>
                <Select value={nuovoCampo.target_key_hotel || "__none__"} onValueChange={(v) => setNuovoCampo({ ...nuovoCampo, target_key_hotel: v === "__none__" ? "" : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TARGET_KEYS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </>
            )}
            <div className="flex items-center gap-2 mt-2">
              <Switch checked={nuovoCampo.required} onCheckedChange={(v) => setNuovoCampo({ ...nuovoCampo, required: v })} />
              <Label className="text-xs">Obbligatorio</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={nuovoCampo.ai_include} onCheckedChange={(v) => setNuovoCampo({ ...nuovoCampo, ai_include: v })} />
              <Label className="text-xs">Includi in AI</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNuovoCampoOpen(false)}>Annulla</Button>
            <Button onClick={addCampo} disabled={busy} data-testid="new-campo-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: modifica campo */}
      <Dialog open={editCampoOpen} onOpenChange={setEditCampoOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Modifica campo</DialogTitle><DialogDescription>key immutabile: <code>{editCampo?.key}</code></DialogDescription></DialogHeader>
          {editCampo && (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              <Label>Label</Label>
              <Input value={editCampo.label || ""} onChange={(e) => setEditCampo({ ...editCampo, label: e.target.value })} />
              <Label>Placeholder</Label>
              <Input value={editCampo.placeholder || ""} onChange={(e) => setEditCampo({ ...editCampo, placeholder: e.target.value })} />
              <Label>Help text</Label>
              <Textarea value={editCampo.help_text || ""} onChange={(e) => setEditCampo({ ...editCampo, help_text: e.target.value })} className="min-h-[60px]" />
              {editCampo.tipo === "number" && (
                <>
                  <Label>Target hotel</Label>
                  <Select value={editCampo.target_key_hotel || "__none__"} onValueChange={(v) => setEditCampo({ ...editCampo, target_key_hotel: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TARGET_KEYS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label>Min</Label><Input type="number" value={editCampo.min ?? ""} onChange={(e) => setEditCampo({ ...editCampo, min: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
                    <div><Label>Max</Label><Input type="number" value={editCampo.max ?? ""} onChange={(e) => setEditCampo({ ...editCampo, max: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
                    <div><Label>Step</Label><Input type="number" value={editCampo.step ?? ""} onChange={(e) => setEditCampo({ ...editCampo, step: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
                  </div>
                </>
              )}
              {editCampo.tipo === "enum" && (
                <>
                  <Label>Opzioni enum (una per riga, formato: value|Label)</Label>
                  <Textarea
                    value={(editCampo.enum_options || []).map((o) => `${o.value}|${o.label}`).join("\n")}
                    onChange={(e) => setEditCampo({ ...editCampo, enum_options: e.target.value.split("\n").filter(Boolean).map((r) => { const [v, l] = r.split("|"); return { value: (v || "").trim(), label: (l || v || "").trim() }; }) })}
                    className="min-h-[100px] font-mono text-xs"
                  />
                </>
              )}
              <div className="flex items-center gap-2 pt-2">
                <Switch checked={!!editCampo.required} onCheckedChange={(v) => setEditCampo({ ...editCampo, required: v })} />
                <Label className="text-xs">Obbligatorio</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editCampo.ai_include !== false} onCheckedChange={(v) => setEditCampo({ ...editCampo, ai_include: v })} />
                <Label className="text-xs">Includi in AI</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editCampo.visibile !== false} onCheckedChange={(v) => setEditCampo({ ...editCampo, visibile: v })} />
                <Label className="text-xs">Visibile nell&apos;editor</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCampoOpen(false)}>Annulla</Button>
            <Button onClick={savePatchCampo} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: reset default */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ripristina schema default</DialogTitle>
            <DialogDescription>
              Tutte le modifiche personalizzate verranno perse. Le relazioni <strong>inviata</strong> conservano il proprio snapshot immutabile e NON vengono toccate. Confermi?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Annulla</Button>
            <Button onClick={resetAll} disabled={busy} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="reset-schema-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Ripristina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfigWidgetEditor
        open={configEditor.open}
        onOpenChange={(o) => setConfigEditor({ open: o, campo: o ? configEditor.campo : null })}
        sezKey={selSez}
        campo={configEditor.campo}
        onSaved={() => load()}
      />
    </div>
  );
}
